/*
 * MyWeeklySchedule widget — mobile-first compact schedule for PMC supervisors.
 *
 * Renders, by default, two prominent lines:
 *   • Your weekly schedule  -- header
 *   • TODAY  (e.g. Mon)     → park chips
 *   • TOMORROW (e.g. Tue)   → park chips
 * Plus a "Show week" toggle that expands into the full 7-day breakdown.
 *
 * All chips show "<code> · <name>" so supervisors recognise parks by
 * name, not just number. The widget renders null when no schedule has
 * been pinned for the current user.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ChevronDown, ChevronUp, CalendarRange, MapPin } from "lucide-react";

const WEEK_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ParkChip({ p, accent = "slate" }) {
  const tone = accent === "today"
    ? "bg-sky-100 text-sky-900 ring-sky-300"
    : accent === "tomorrow"
      ? "bg-emerald-50 text-emerald-900 ring-emerald-200"
      : "bg-slate-100 text-slate-700 ring-slate-200";
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ring-1 ${tone}`}
      title={p.notes || `${p.park_code} · ${p.park_name}`}
    >
      <MapPin className="h-3 w-3 flex-none" />
      <span className="truncate">
        {p.park_code ? `${p.park_code} · ` : ""}{p.park_name}
      </span>
    </span>
  );
}

function DayLine({ label, sub, parks, accent }) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex-none">
        <div className={`text-[10px] font-semibold uppercase tracking-wider ${
          accent === "today" ? "text-sky-800" : accent === "tomorrow" ? "text-emerald-800" : "text-slate-500"
        }`}>
          {label}
        </div>
        {sub ? (
          <div className="text-[10px] text-slate-500">{sub}</div>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 pt-0.5">
        {parks.length === 0 ? (
          <span className="text-[12px] italic text-muted-foreground">No visits</span>
        ) : (
          parks.map((p) => <ParkChip key={p.park_id} p={p} accent={accent} />)
        )}
      </div>
    </div>
  );
}

export function MyWeeklySchedule() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/me/visit-schedule")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, []);

  // Compute "tomorrow" parks from the same by_day map the backend returns.
  const tomorrow = useMemo(() => {
    if (!data) return { label: null, parks: [] };
    const byDay = data.by_day || {};
    const todayIdx = WEEK_ORDER.indexOf(data.weekday_label);
    if (todayIdx < 0) return { label: null, parks: [] };
    const tomLabel = WEEK_ORDER[(todayIdx + 1) % 7];
    return { label: tomLabel, parks: byDay[tomLabel] || [] };
  }, [data]);

  if (err || !data) return null;

  const today = data.today_parks || [];
  const byDay = data.by_day || {};
  const hasAny = today.length > 0
    || tomorrow.parks.length > 0
    || WEEK_ORDER.some((d) => (byDay[d] || []).length > 0);
  if (!hasAny) return null;

  const todayLabel = data.weekday_label || "Today";

  return (
    <Card
      className="border-sky-200 bg-sky-50/60 p-2.5"
      data-testid="my-weekly-schedule"
    >
      <div className="flex items-center gap-2">
        <CalendarRange className="h-4 w-4 flex-none text-sky-700" />
        <div className="flex-1 text-[12px] font-semibold uppercase tracking-wider text-sky-800">
          Your weekly schedule
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-sky-800 hover:bg-sky-100"
          aria-expanded={open}
          data-testid="schedule-toggle"
        >
          {open ? "Hide week" : "Show week"}
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* TODAY + TOMORROW — always visible */}
      <div className="mt-2 space-y-1.5">
        <DayLine
          label={`Today · ${todayLabel}`}
          sub={data.today_iso}
          parks={today}
          accent="today"
        />
        {tomorrow.label ? (
          <DayLine
            label={`Tomorrow · ${tomorrow.label}`}
            parks={tomorrow.parks}
            accent="tomorrow"
          />
        ) : null}
      </div>

      {/* Full week — toggleable */}
      {open ? (
        <div
          className="mt-2 border-t border-sky-200 pt-2"
          data-testid="schedule-weekly-list"
        >
          <ul className="space-y-1">
            {WEEK_ORDER.map((d) => {
              const parks = byDay[d] || [];
              const isToday = d === todayLabel;
              const isTomorrow = d === tomorrow.label;
              return (
                <li
                  key={d}
                  className={`flex items-start gap-2 rounded px-1.5 py-1 text-sm ${
                    isToday ? "bg-sky-50 ring-1 ring-sky-200" : isTomorrow ? "bg-emerald-50/70 ring-1 ring-emerald-200" : ""
                  }`}
                >
                  <span
                    className={`inline-flex h-5 min-w-[36px] items-center justify-center rounded text-[10px] font-semibold ${
                      isToday
                        ? "bg-sky-200 text-sky-900"
                        : isTomorrow
                          ? "bg-emerald-200 text-emerald-900"
                          : "bg-white text-slate-700 ring-1 ring-slate-200"
                    }`}
                  >
                    {d}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                    {parks.length === 0 ? (
                      <span className="text-[12px] italic text-muted-foreground">No visits</span>
                    ) : (
                      parks.map((p) => (
                        <ParkChip
                          key={p.park_id}
                          p={p}
                          accent={isToday ? "today" : isTomorrow ? "tomorrow" : "slate"}
                        />
                      ))
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-1.5 text-right text-[10px] text-sky-700/70">
            Pinned by admin
          </div>
        </div>
      ) : null}
    </Card>
  );
}
